<template>
  <Card :title="$t('global.purge-cache')">
    <LoadingState v-if="isLoading()" :size="50" />
    <ErrorState v-else-if="isErrored()" />
    <template v-else-if="site">
      <CardActionContent :buttons="getButtons()">
        <template #description>
          <div>
            <p>
              {{
                $t(
                  isCdnEnabled
                    ? "global.purge-cache-with-cdn-description"
                    : "global.purge-cache-description"
                )
              }}
            </p>
            <p v-if="!canPurgeCache()">
              {{
                $t(
                  isCdnEnabled
                    ? "global.purge-cache-with-cdn-disabled-description"
                    : "global.purge-cache-disabled-description"
                )
              }}
            </p>
          </div>
        </template>
      </CardActionContent>
    </template>
  </Card>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import Card from "@/components/display/Card";
import CardActionContent from "@/components/display/card-sub-components/CardActionContent";
import ErrorState from "@/components/display/ErrorState";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import store from "@/store";

export default {
  name: "PurgeCache",
  components: {
    Card,
    CardActionContent,
    ErrorState,
    LoadingState,
  },
  props: {
    namespace: {
      type: String,
      required: true,
    },
    plan: Object,
    planStatus: {
      type: Object,
      required: true,
    },
    site: Object,
    siteStatus: {
      type: Object,
      required: true,
    },
  },
  setup(props, { root }) {
    const namespace = props.namespace;
    const cdn = computed(() => store.getters[`${namespace}/getCdn`]);
    const cdnStatus = computed(() => store.getters[`${namespace}/getCdnStatus`]);
    const isCdnEnabled = computed(() => cdn.value?.enabled);
    const varnishEnabled = computed(
      () => store.getters["containers/getVarnish"]?.state === "stable"
    );

    const canPurgeCache = () =>
      props.site.environment.options.nginxEnabled || varnishEnabled.value || isCdnEnabled.value;

    const getButtons = () => [
      new NavLink(
        root.$t("global.purge-cache"),
        null,
        purge,
        false,
        "primary",
        null,
        isSubmitting.value || !cdnStatus.value.isLoaded || !canPurgeCache(),
        isSubmitting.value
      ),
    ];

    const isErrored = () =>
      props.planStatus.isErrored || props.siteStatus.isErrored || cdnStatus.value.isErrored;
    const isLoading = () =>
      props.planStatus.isLoading ||
      props.siteStatus.isLoading ||
      cdnStatus.value.isLoading ||
      cdnStatus.value.isUpdating;

    const isSubmitting = ref(false);
    const purge = () => {
      isSubmitting.value = true;
      store
        .dispatch("sites/purgeCache", { id: props.site.id })
        .then(() => {
          if (isCdnEnabled.value) {
            return store.dispatch(`${namespace}/purgeCdn`, props.site?.id);
          }
        })
        .then(() => {
          store.dispatch("toasts/addToast", {
            variant: "success",
            message: root.$t("toasts.purge-cache-success-message", { domain: props.site.domain }),
          });
        })
        .catch(() => {
          store.dispatch("toasts/addToast", {
            variant: "error",
            message: root.$t("toasts.purge-cache-error-message", { domain: props.site.domain }),
          });
        })
        .finally(() => (isSubmitting.value = false));
    };

    onMounted(() => {
      store.dispatch(`${namespace}/getCdn`, props.site?.id);
    });

    return {
      canPurgeCache,
      getButtons,
      isCdnEnabled,
      isErrored,
      isLoading,
    };
  },
};
</script>
